import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import { useSupabase } from '@/hooks/useSupabase'
import type { Factura } from '@/lib/types'
import {
  IconCheckCircle, IconAlertCircle, IconRefresh,
  IconDownload, IconExternalLink, IconFile,
} from '@/components/icons/Icons'
import Modal from '@/components/layout/Modal'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface SUNATPanelProps {
  factura: Factura
  onUpdate: (updated: Factura) => void
}

const SUNAT_BADGE: Record<string, { label: string; cssClass: string }> = {
  no_emitida: { label: 'Pendiente SUNAT', cssClass: 'sunat-badge sunat-badge--pending' },
  emitiendo: { label: 'Emitiendo...', cssClass: 'sunat-badge sunat-badge--pending' },
  emitida: { label: 'Emitida SUNAT', cssClass: 'sunat-badge sunat-badge--emitida' },
  error: { label: 'Error SUNAT', cssClass: 'sunat-badge sunat-badge--error' },
  anulada: { label: 'Anulada SUNAT', cssClass: 'sunat-badge sunat-badge--error' },
}

/** Max days old for fecha_emision before requiring update */
const MAX_DATE_AGE_DAYS = 2

/** Calculate days between two date strings (YYYY-MM-DD) and today */
function daysSince(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00')
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
}

/** Format YYYY-MM-DD to DD/MM/YYYY for display */
function formatDateDMY(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${d}/${m}/${y}`
}

export default function SUNATPanel({ factura, onUpdate }: SUNATPanelProps) {
  const db = useSupabase()
  const [emitting, setEmitting] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [voidMotivo, setVoidMotivo] = useState('')
  const [showDateModal, setShowDateModal] = useState(false)
  const [updatingDate, setUpdatingDate] = useState(false)

  const estado = factura.sunat_estado || 'no_emitida'
  const badge = SUNAT_BADGE[estado] || SUNAT_BADGE.no_emitida
  const isEmitted = estado === 'emitida'
  const isError = estado === 'error'
  const isEmitting = estado === 'emitiendo' || emitting
  const canVoid = estado === 'emitida'

  /** Gate: validate date before emitting */
  function handleEmitClick() {
    if (!factura.fecha_emision) {
      // No date set — emit directly
      handleEmit()
      return
    }
    const age = daysSince(factura.fecha_emision)
    if (age > MAX_DATE_AGE_DAYS) {
      setShowDateModal(true)
      return
    }
    handleEmit()
  }

  /** Update fecha_emision to today, then emit */
  async function handleUpdateDateAndEmit() {
    setUpdatingDate(true)
    try {
      const today = new Date().toISOString().split('T')[0]
      await db.update('facturas', { id: factura.id, fecha_emision: today })
      // Refresh factura so handleEmit uses the updated date
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) onUpdate(updated)
      setShowDateModal(false)
      showToast(`Fecha de emisión actualizada a ${formatDateDMY(today)}`, 'success')
      // Emit with updated data
      handleEmit()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error actualizando fecha'
      showToast(message, 'error')
    } finally {
      setUpdatingDate(false)
    }
  }

  async function handleEmit() {
    setEmitting(true)
    try {
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) throw new Error('No autenticado')

      const res = await fetch(
        `${API_URL}/api/facturas/${factura.id}/nubefact-emit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Error emitiendo comprobante')

      showToast(`Comprobante ${result.serie}-${String(result.numero).padStart(8, '0')} emitido exitosamente`, 'success')

      // Auto-notify client via email
      try {
        const notifyRes = await fetch(
          `${API_URL}/api/notifications/send`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ evento: 'factura_emitida', variables: { numero: factura.numero } }),
          }
        )
        if (notifyRes.ok) {
          showToast('Notificación enviada al cliente', 'success')
        }
      } catch {
        // Non-blocking: emission succeeded even if notification fails
        console.warn('Auto-notification failed after emission')
      }

      // Refresh factura data
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) onUpdate(updated)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error emitiendo comprobante'
      showToast(message, 'error')
      // Refresh to get error state
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) onUpdate(updated)
    } finally {
      setEmitting(false)
    }
  }

  async function handleVoid() {
    if (!voidMotivo.trim()) {
      showToast('Ingresa el motivo de anulacion', 'error')
      return
    }
    setVoiding(true)
    try {
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) throw new Error('No autenticado')

      const res = await fetch(
        `${API_URL}/api/facturas/${factura.id}/nubefact-anular`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ motivo: voidMotivo }),
        }
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Error anulando comprobante')

      showToast(result.message || 'Comprobante anulado', 'success')
      setShowVoidModal(false)
      setVoidMotivo('')

      // Refresh factura data
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) onUpdate(updated)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error anulando comprobante'
      showToast(message, 'error')
    } finally {
      setVoiding(false)
    }
  }

  return (
    <>
      <div className="sunat-panel">
        <div className="sunat-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <path d="M9 15l2 2 4-4"/>
            </svg>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Facturacion Electronica SUNAT</span>
          </div>
          <span className={badge.cssClass}>{badge.label}</span>
        </div>

        {/* Emitted — show documents */}
        {isEmitted && (
          <div className="sunat-panel-body">
            {factura.sunat_serie && factura.sunat_numero && (
              <div className="sunat-info-row">
                <span className="sunat-info-label">Comprobante</span>
                <span className="sunat-info-value" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {factura.sunat_serie}-{String(factura.sunat_numero).padStart(8, '0')}
                </span>
              </div>
            )}
            {factura.sunat_hash && (
              <div className="sunat-info-row">
                <span className="sunat-info-label">Hash CPE</span>
                <span className="sunat-info-value" style={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                  {factura.sunat_hash.length > 40 ? factura.sunat_hash.slice(0, 40) + '...' : factura.sunat_hash}
                </span>
              </div>
            )}

            <div className="sunat-download-links">
              {factura.sunat_pdf_url && (
                <a href={factura.sunat_pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IconDownload size={13} /> PDF SUNAT
                </a>
              )}
              {factura.sunat_xml_url && (
                <a href={factura.sunat_xml_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IconFile size={13} /> XML
                </a>
              )}
              {factura.sunat_cdr_url && (
                <a href={factura.sunat_cdr_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IconFile size={13} /> CDR
                </a>
              )}
              {factura.sunat_enlace_consulta && (
                <a href={`https://www.sunat.gob.pe/ol-ti-itconsultaunificadalibre/consultaUnificadaLibre/consulta?numRuc=${(factura.sunat_enlace_consulta || '').split('|')[0].trim()}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <IconExternalLink size={13} /> Verificar SUNAT
                </a>
              )}
            </div>

            {/* Void button for emitted invoices */}
            {canVoid && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setShowVoidModal(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <IconAlertCircle size={13} /> Anular en SUNAT
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="sunat-panel-body">
            <div className="sunat-error-box">
              <IconAlertCircle size={16} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Error al emitir</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {factura.sunat_error || 'Error desconocido'}
                </div>
              </div>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleEmitClick}
              disabled={isEmitting}
              style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconRefresh size={13} /> {isEmitting ? 'Reintentando...' : 'Reintentar Emision'}
            </button>
          </div>
        )}

        {/* Not emitted — show emit button */}
        {estado === 'no_emitida' && (
          <div className="sunat-panel-body">
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
              Esta factura aun no ha sido emitida como comprobante electronico ante SUNAT via NubeFact.
            </p>
            <button
              className="btn btn-primary"
              onClick={handleEmitClick}
              disabled={isEmitting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <IconCheckCircle size={16} />
              {isEmitting ? 'Emitiendo...' : 'Emitir a SUNAT'}
            </button>
          </div>
        )}

        {/* Emitting state */}
        {isEmitting && estado !== 'no_emitida' && estado !== 'error' && (
          <div className="sunat-panel-body" style={{ textAlign: 'center', padding: '24px 16px' }}>
            <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Emitiendo comprobante electronico a SUNAT...
            </div>
          </div>
        )}

        {/* Anulada state */}
        {estado === 'anulada' && (
          <div className="sunat-panel-body">
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Este comprobante fue anulado ante SUNAT.
              {factura.sunat_serie && factura.sunat_numero && (
                <span style={{ fontFamily: 'monospace', marginLeft: 4 }}>
                  ({factura.sunat_serie}-{String(factura.sunat_numero).padStart(8, '0')})
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Void confirmation modal */}
      <Modal isOpen={showVoidModal} onClose={() => setShowVoidModal(false)} title="Anular Comprobante SUNAT">
        <p style={{ marginBottom: 16 }}>
          Vas a anular el comprobante <strong>{factura.sunat_serie}-{String(factura.sunat_numero || 0).padStart(8, '0')}</strong> ante SUNAT.
          Esta accion es irreversible.
        </p>
        <div className="form-group">
          <label className="form-label">Motivo de anulacion *</label>
          <textarea
            className="form-textarea"
            value={voidMotivo}
            onChange={e => setVoidMotivo(e.target.value)}
            rows={3}
            placeholder="Ej: Error en los datos del cliente, error en montos..."
          />
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setShowVoidModal(false)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleVoid} disabled={voiding}>
            {voiding ? 'Anulando...' : 'Anular Comprobante'}
          </button>
        </div>
      </Modal>

      {/* Date too old confirmation modal */}
      <Modal isOpen={showDateModal} onClose={() => setShowDateModal(false)} title="Fecha de Emisión Antigua">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <IconAlertCircle size={20} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>
              La fecha de emisión es <strong>{factura.fecha_emision ? formatDateDMY(factura.fecha_emision) : '—'}</strong>{' '}
              ({factura.fecha_emision ? daysSince(factura.fecha_emision) : 0} días atrás).
            </p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              SUNAT rechaza comprobantes con fecha de emisión mayor a {MAX_DATE_AGE_DAYS} días.
              ¿Deseas actualizar la fecha a <strong>hoy</strong> y emitir?
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setShowDateModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleUpdateDateAndEmit} disabled={updatingDate}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconCheckCircle size={14} />
            {updatingDate ? 'Actualizando...' : 'Actualizar Fecha y Emitir'}
          </button>
        </div>
      </Modal>
    </>
  )
}
